import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { PermissionName } from 'app/helpers/app-constants';
import { LoggedOnMerchantSummaryStore } from 'app/store/merchant.store';
import { LoggedOnUserPermissionsStore } from 'app/store/permissions.auth.store';
import { environment } from 'environments/environment';
import { filter, map, Observable, ReplaySubject, shareReplay, switchMap, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);
    permissionStore = inject(LoggedOnUserPermissionsStore);
    merchantStore = inject(LoggedOnMerchantSummaryStore)
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return toObservable(computed(() => ({
                permissionLoaded: this.permissionStore.isLoaded(),
                isAmexColleague: this.permissionStore.isAmexColleague(),
                merchantLoaded: this.merchantStore.isLoaded()
            }))).pipe(
                filter(({permissionLoaded, isAmexColleague, merchantLoaded}) => permissionLoaded && (isAmexColleague || merchantLoaded)),
                take(1),
                switchMap(() => this._httpClient.get<Navigation>(`${environment.apiUrl}/common/user-navigations`)),
                tap((navigation) => {
                    const linkSet = new Set<string>();
                    Object.values(navigation).forEach(navArray => this.filterItems(navArray, linkSet));
                    
                    this.permissionStore.setNavigations(linkSet);
                    this._navigation.next(navigation);
                }),
            shareReplay(1)
        );
    }

    getMerchantNavigation(merchantApiId: string) : Observable<Navigation> {

        return toObservable(computed(() => ({
                permissionLoaded: this.permissionStore.isLoaded(),
                isAmexColleague: this.permissionStore.isAmexColleague(),
                merchantLoaded: this.merchantStore.isLoaded()
            }))).pipe(
                filter(({permissionLoaded, isAmexColleague, merchantLoaded}) => permissionLoaded && (isAmexColleague || merchantLoaded)),
                take(1),
                switchMap(() => this._httpClient.get<Navigation>(`${environment.apiUrl}/common/merchants/${merchantApiId}/navigations`)),
                map((navigations) => {
                    const linkSet = new Set<string>();

                    const mergedNav: Navigation = {
                        compact: navigations.compact,
                        default: navigations.default,
                        futuristic: navigations.futuristic,
                        horizontal: navigations.horizontal
                    }

                    Object.values(mergedNav).forEach(navArray => this.filterItems(navArray, linkSet, merchantApiId));
                    
                    this.permissionStore.setNavigations(linkSet)
                    this._navigation.next(mergedNav);

                    return mergedNav;
                }),
            shareReplay(1)
        );
    }

    filterItems(items: FuseNavigationItem[], linkSet: Set<string>, merchantApiId: string | void) {
        items.forEach(item => {

            if (item.id === 'multiple') {
                const allowedSet: Set<string> = new Set([
                    PermissionName.MerchantDashboard,
                    PermissionName.User,
                    PermissionName.Transaction
                ]);

                item.children.forEach(c => {
                    c.hidden = () => !allowedSet.has(c.id)
                })

                item.children.forEach(c => {
                    if (c.id === PermissionName.MerchantDashboard) {
                        c.title = 'Merchants'
                        c.link = '/merchant/list'
                    }
                })
            } else if (item.id === 'merchant') {
                const allowedSet: Set<string> = new Set([
                    PermissionName.MerchantDashboard,
                    PermissionName.Merchant,
                    PermissionName.MerchantSetting,
                    PermissionName.MerchantPricing,
                    PermissionName.MerchantPaymentPage
                ]);

                item.children.forEach(c => {
                    c.hidden = () => !allowedSet.has(c.id)
                    c.link = `${c.link}/${merchantApiId}`
                })
            } else if (item.id === 'colleague') {
                item.title = 'AEPS Admin'
            }

            if (!!item.link) {
                linkSet.add(item.link)
            }

            if (item.children) {
                this.filterItems(item.children, linkSet);
            }
        });

        return linkSet;
    }
}
